import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Plus, Trash2, Unlock, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { coursesAPI, enrollAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';
import {
  Badge, DataTable, EmptyPanel, FilterPills, PageHeader, PersonCell, RowMenu, SearchBox, SelectBox,
  StatCards, TableRow, Thumb, adminBtnOutline, adminBtnPrimary, fmtDate,
} from '../../components/adminUi';

// Admin 4 · Enrollments (s134 redesign): stat cards, source pills with counts,
// search + course filter, table with member avatar, course thumb, progress bar,
// source badge, readable date and a ⋮ Unenroll.
// s135 access workflow: status pills (Pending / Awaiting release / Active),
// Approve · Reject on registrations, and "Release next" per student — lessons
// reach a student one at a time, only when staff release them.
const COLS = [
  { label: 'Member', width: 'minmax(0,2fr)' },
  { label: 'Course', width: 'minmax(0,2.2fr)' },
  { label: 'Progress', width: '190px' },
  { label: 'Status', width: '150px' },
  { label: 'Enrolled', width: '110px' },
  { label: 'Actions', width: '240px', align: 'right' },
];
const rowBtn = 'h-9 px-3 text-[13px]';
const STATUS_PILLS = ['', 'pending', 'awaiting', 'active'];

export default function EnrollmentsPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  // ?status= deep link from the notification emails (pending / awaiting)
  const [status, setStatus] = useState(() => {
    const s = params.get('status') || '';
    return STATUS_PILLS.includes(s) ? s : '';
  });
  const [acting, setActing] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [filterCourses, setFilterCourses] = useState([]);
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [courses, setCourses] = useState([]);
  const [pool, setPool] = useState([]);
  const [poolQ, setPoolQ] = useState('');
  const [pick, setPick] = useState(null); // {mid, name, email}
  const [courseId, setCourseId] = useState('');
  const [busy, setBusy] = useState(false);
  const perPage = 25;

  const load = useCallback(() => {
    enrollAPI.list({ search, source, status, course_id: courseFilter, page, per_page: perPage })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error(t('Could not load enrollments')));
  }, [search, source, status, courseFilter, page]);

  const pickStatus = (v) => {
    setStatus(v);
    setPage(1);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set('status', v); else next.delete('status');
      return next;
    }, { replace: true });
  };

  const act = async (row, fn, okMsg) => {
    setActing(row.id);
    try {
      await fn(row.id);
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setActing('');
    }
  };
  const approve = (row) => act(row, enrollAPI.approve, t('Registration approved'));
  const reject = (row) => {
    if (!window.confirm(`${t('Reject the registration of')} ${(row.member || {}).name || row.member_id}?`)) return;
    act(row, enrollAPI.reject, t('Registration rejected'));
  };
  const releaseNext = (row) => act(row, enrollAPI.releaseNext, t('Lesson released'));
  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  useEffect(() => {
    coursesAPI.list({ per_page: 100 }).then(({ data: d }) => setFilterCourses(d.courses || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!adding) return;
    coursesAPI.list({ per_page: 100 }).then(({ data: d }) => setCourses(d.courses)).catch(() => {});
  }, [adding]);

  useEffect(() => {
    if (!adding) return undefined;
    const timer = setTimeout(() => {
      enrollAPI.memberPool(poolQ).then(({ data: d }) => setPool(d)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [adding, poolQ]);

  const create = async (e) => {
    e.preventDefault();
    if (!pick || !courseId) return;
    setBusy(true);
    try {
      await enrollAPI.create({ member_id: pick.mid, course_id: courseId });
      toast.success(t('Enrolled'));
      setAdding(false);
      setPick(null);
      setPoolQ('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`${t('Remove enrollment for')} ${(row.member || {}).name || row.member_id}?`)) return;
    try {
      await enrollAPI.remove(row.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / perPage)) : 1;
  const sm = (data && data.summary) || null;
  const bySt = (sm && sm.by_status) || {};
  const SOURCE = { admin: 'Admin', purchase: 'Purchase', free: 'Free', register: 'Registration' };

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader title={t('Enrollments')} subtitle={t('Who is taking which course, how far they are, and how they got in.')}>
        <button type="button" onClick={() => setAdding(true)} className={adminBtnPrimary}>
          <Plus size={16} strokeWidth={2.4} /> {t('Enroll a member')}
        </button>
      </PageHeader>

      {sm && (
        <StatCards
          items={[
            { label: t('Enrollments'), value: sm.total },
            { label: t('Students'), value: sm.students, note: t('unique members') },
            { label: t('Average progress'), value: `${sm.avg_progress}%` },
            { label: t('Completed'), value: sm.completed, note: t('courses at 100%') },
          ]}
        />
      )}

      <FilterPills
        value={status}
        onChange={pickStatus}
        items={[
          { key: '', label: t('All'), count: sm ? sm.total : undefined },
          { key: 'pending', label: t('Pending'), count: bySt.pending || 0 },
          { key: 'awaiting', label: t('Awaiting release'), count: bySt.awaiting || 0 },
          { key: 'active', label: t('Active'), count: bySt.active || 0 },
        ]}
      >
        <SearchBox value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('Search member or email…')} />
        <SelectBox value={source} onChange={(v) => { setSource(v); setPage(1); }} label={t('Source')}>
          <option value="">{t('All sources')}</option>
          {Object.entries(SOURCE).map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
        </SelectBox>
        {filterCourses.length > 0 && (
          <SelectBox value={courseFilter} onChange={(v) => { setCourseFilter(v); setPage(1); }} label={t('Course')}>
            <option value="">{t('All courses')}</option>
            {filterCourses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </SelectBox>
        )}
      </FilterPills>

      {!data ? (
        <p className="text-sm text-lms-muted">{t('Loading…')}</p>
      ) : (
        <DataTable
          cols={COLS}
          empty={data.enrollments.length === 0 ? (
            <EmptyPanel bare icon={Users} title={search || source || status || courseFilter ? t('No enrollments match these filters.') : t('No enrollments yet.')} />
          ) : null}
        >
          {data.enrollments.map((r) => {
            const pct = r.progress_pct || 0;
            const rel = r.release || {};
            const pending = r.status === 'pending';
            const busyRow = acting === r.id;
            return (
              <TableRow key={r.id} cols={COLS}>
                <PersonCell name={(r.member || {}).name} email={(r.member || {}).email} />
                <div className="flex min-w-0 items-center gap-3">
                  <Thumb src={(r.course || {}).thumbnail} title={(r.course || {}).title} />
                  <span className="truncate text-sm text-lms-ink">{(r.course || {}).title || '—'}</span>
                </div>
                {pending ? (
                  <span className="text-[13px] text-lms-sub">{t(SOURCE[r.source] || 'Registration')}</span>
                ) : (
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex items-center gap-2.5">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-lms-line">
                        <div className="h-full bg-lms-accent" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-9 text-right text-[13px] font-semibold text-lms-ink lms-num">{pct}%</span>
                    </div>
                    <span className="text-xs text-lms-sub lms-num">
                      {t('Lessons released')}: {rel.released || 0}/{rel.total || 0}
                    </span>
                  </div>
                )}
                <span className="flex min-w-0 flex-col items-start gap-1">
                  {pending ? <Badge kind="warn">{t('Pending')}</Badge>
                    : rel.awaiting_release ? <Badge kind="info">{t('Awaiting release')}</Badge>
                      : <Badge kind="ok">{t('Active')}</Badge>}
                  {rel.awaiting_release && rel.next_lesson_title && (
                    <span className="lms-clamp2 text-xs text-lms-sub" title={rel.next_lesson_title}>
                      {t('Next')}: {rel.next_lesson_title}
                    </span>
                  )}
                </span>
                <span className="text-[13px] text-lms-sub">{fmtDate(pending ? (r.requested_at || r.enrolled_at) : r.enrolled_at)}</span>
                <span className="flex items-center justify-end gap-2">
                  {pending ? (
                    <>
                      <button type="button" disabled={busyRow} onClick={() => reject(r)} className={`${adminBtnOutline} ${rowBtn}`}>
                        <X size={14} /> {t('Reject')}
                      </button>
                      <button type="button" disabled={busyRow} onClick={() => approve(r)} className={`${adminBtnPrimary} ${rowBtn}`}>
                        <Check size={14} strokeWidth={2.6} /> {t('Approve')}
                      </button>
                    </>
                  ) : rel.next_lesson_id ? (
                    <button
                      type="button"
                      disabled={busyRow}
                      onClick={() => releaseNext(r)}
                      title={rel.next_lesson_title}
                      className={`${rel.awaiting_release ? adminBtnPrimary : adminBtnOutline} ${rowBtn}`}
                    >
                      <Unlock size={14} /> {t('Release next')}
                    </button>
                  ) : (
                    <span className="text-xs text-lms-sub">{t('All released')}</span>
                  )}
                  {!pending && <RowMenu items={[{ label: t('Unenroll'), icon: Trash2, onClick: () => del(r), danger: true }]} />}
                </span>
              </TableRow>
            );
          })}
        </DataTable>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className={adminBtnOutline}>{t('Previous')}</button>
          <span className="text-sm text-lms-sub lms-num">{page} / {pages}</span>
          <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} className={adminBtnOutline}>{t('Next')}</button>
        </div>
      )}

      {adding && (
        <Modal title={t('Enroll a member')} onClose={() => setAdding(false)}>
          <form onSubmit={create} className="space-y-4">
            <Field label={t('Member')}>
              {pick ? (
                <div className="flex items-center justify-between rounded-md border border-lms-border bg-lms-raised px-3 py-2 text-sm">
                  <span>{pick.name} <span className="text-lms-faint">{pick.email}</span></span>
                  <button type="button" onClick={() => setPick(null)} className="text-lms-muted hover:text-lms-text">{t('Change')}</button>
                </div>
              ) : (
                <div>
                  <input
                    value={poolQ}
                    onChange={(e) => setPoolQ(e.target.value)}
                    placeholder={t('Search members…')}
                    autoFocus
                    className={inputCls}
                  />
                  <div className="mt-1 max-h-44 overflow-y-auto rounded-md border border-lms-border">
                    {pool.map((m) => (
                      <button
                        key={m.mid}
                        type="button"
                        onClick={() => setPick(m)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-lms-muted hover:bg-lms-raised hover:text-lms-text"
                      >
                        <span>{m.name}</span>
                        <span className="text-xs text-lms-faint">{m.email}</span>
                      </button>
                    ))}
                    {pool.length === 0 && <p className="px-3 py-2 text-xs text-lms-faint">{t('No matches')}</p>}
                  </div>
                </div>
              )}
            </Field>
            <Field label={t('Course')}>
              <select value={courseId} onChange={(e) => setCourseId(e.target.value)} required className={inputCls}>
                <option value="">—</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setAdding(false)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy || !pick || !courseId} className={btnPrimary}>
                {busy ? t('Saving…') : t('Enroll')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
